// components/ai-chat/AIChatLauncher.tsx
'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useClientSnapshot } from '@/hooks/useClientSnapshot';
import { useProductStickyCtaVisible } from '@/hooks/useProductStickyCtaVisible';

const chatButtonClassName =
  'fixed right-4 z-50 flex h-12 min-w-12 items-center justify-center overflow-hidden rounded-[18px] border border-white/10 bg-gradient-to-br from-[#7a3039] via-[#3a151c] to-[#111827] px-3 text-white shadow-[0_16px_36px_-22px_rgba(0,0,0,0.95),0_0_0_1px_rgba(255,255,255,0.05)_inset] backdrop-blur-md transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-22px_rgba(0,0,0,0.95),0_0_0_1px_rgba(255,255,255,0.08)_inset] active:translate-y-0 active:scale-95 motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100 md:bottom-6 md:right-6 md:h-14 md:min-w-14 md:rounded-[20px]';

function useChatButtonOffsetClassName() {
  const pathname = usePathname();
  const isProductPage = pathname.startsWith('/products/');
  const isProductCtaVisible = useProductStickyCtaVisible();

  return isProductPage && isProductCtaVisible ? 'bottom-[8.75rem] md:bottom-6' : 'bottom-4';
}

function ChatButtonContent() {
  return (
    <>
      <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_32%_18%,rgba(255,255,255,0.24),transparent_34%)]" />
      <span className="relative flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-[#f5d6dc]" strokeWidth={2.25} aria-hidden />
        <span className="text-[13px] font-bold tracking-[0.02em] md:text-sm">AI</span>
      </span>
    </>
  );
}

function LoadingChatButton() {
  const offsetClassName = useChatButtonOffsetClassName();

  return (
    <button
      type="button"
      disabled
      className={`${chatButtonClassName} ${offsetClassName}`}
      aria-label="Loading AI chat"
    >
      <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_32%_18%,rgba(255,255,255,0.24),transparent_34%)]" />
      <span className="relative h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
    </button>
  );
}

const AIChat = dynamic(
  () => import('./index').then(mod => mod.AIChat),
  {
    ssr: false,
    loading: () => <LoadingChatButton />,
  }
);

const SIGN_IN_RESTORE_KEY = 'amster_sommelier_sign_in';

// A chat that sent the shopper to sign in reopens when they return to the same page.
function hasSignInRestore(): boolean {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SIGN_IN_RESTORE_KEY) || 'null');
    return saved?.path === window.location.pathname + window.location.search && Date.now() - saved.timestamp < 10 * 60 * 1000;
  } catch {
    return false; // Storage can be unavailable in private browsing.
  }
}

export function AIChatLauncher() {
  const [hasActivatedChat, setHasActivatedChat] = useState(false);
  const offsetClassName = useChatButtonOffsetClassName();
  const shouldRestoreChat = useClientSnapshot(hasSignInRestore, false);

  // AIChat consumes the saved entry when it mounts, so latch the restore here.
  if (shouldRestoreChat && !hasActivatedChat) {
    setHasActivatedChat(true);
  }

  // Drop a stale entry left by another page or an expired sign-in.
  useEffect(() => {
    if (hasSignInRestore()) return;
    try {
      sessionStorage.removeItem(SIGN_IN_RESTORE_KEY);
    } catch { /* Storage can be unavailable in private browsing. */ }
  }, []);

  if (hasActivatedChat) {
    return <AIChat initialOpen />;
  }

  return (
    <button
      type="button"
      onClick={() => setHasActivatedChat(true)}
      className={`${chatButtonClassName} ${offsetClassName}`}
      aria-label="Open AI Chat"
    >
      <ChatButtonContent />
    </button>
  );
}
